import { useCallback, useEffect, useMemo, useState } from "react"
import {
  Button,
  Form,
  Input,
  Modal,
  Popconfirm,
  Segmented,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  message,
} from "antd"
import type { ColumnsType } from "antd/es/table"
import {
  adminApprovePartner,
  adminCreatePartner,
  adminListPartners,
  adminSuspendPartner,
  adminUpdatePartner,
  type PartnerRow,
} from "../templates-admin/templates-admin-api.service"

const STATUS_COLOR: Record<string, string> = {
  approved: "green",
  pending: "gold",
  suspended: "red",
}

export default function PartnersManagementPage() {
  const [rows, setRows] = useState<PartnerRow[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const [createOpen, setCreateOpen] = useState(false)
  const [statusFilter, setStatusFilter] = useState<"all" | "pending" | "approved" | "suspended">("all")
  const [form] = Form.useForm()

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await adminListPartners({ page, limit: 30 })
      setRows(res.data)
      setTotal(res.total)
    } catch {
      message.error("Failed to load partners")
    } finally {
      setLoading(false)
    }
  }, [page])

  useEffect(() => {
    load()
  }, [load])

  const pendingCount = useMemo(
    () => rows.filter((r) => r.status === "pending").length,
    [rows]
  )

  const filteredRows = useMemo(
    () =>
      statusFilter === "all"
        ? rows
        : rows.filter((r) => (r.status ?? "pending") === statusFilter),
    [rows, statusFilter]
  )

  const columns: ColumnsType<PartnerRow> = [
    { title: "Name", dataIndex: "name", key: "name" },
    { title: "Slug", dataIndex: "slug", key: "slug" },
    { title: "Email", dataIndex: "contactEmail", key: "contactEmail" },
    {
      title: "Status",
      key: "status",
      width: 110,
      render: (_, r) =>
        r.status ? (
          <Tag color={STATUS_COLOR[r.status] ?? "default"}>{r.status}</Tag>
        ) : (
          <Tag>—</Tag>
        ),
    },
    {
      title: "Rev-share",
      key: "commissionPct",
      width: 90,
      render: (_, r) =>
        r.commissionPct != null ? `${Number(r.commissionPct)}%` : "—",
    },
    {
      title: "Prompts",
      key: "promptsActive",
      width: 90,
      render: (_, r) => (
        <Tooltip title="Surface this influencer's prompts in Claude/ChatGPT by handle">
          <Switch
            checked={!!r.promptsActive}
            onChange={async (v) => {
              try {
                await adminUpdatePartner(r.id, { promptsActive: v })
                message.success("Updated")
                load()
              } catch {
                message.error("Failed")
              }
            }}
          />
        </Tooltip>
      ),
    },
    {
      title: "Active",
      key: "isActive",
      width: 90,
      render: (_, r) => (
        <Switch
          checked={r.isActive}
          onChange={async (v) => {
            try {
              await adminUpdatePartner(r.id, { isActive: v })
              message.success("Updated")
              load()
            } catch {
              message.error("Failed")
            }
          }}
        />
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 200,
      render: (_, r) => (
        <Space>
          {r.status !== "approved" && (
            <Popconfirm
              title="Approve this affiliate? They can then receive payouts."
              okText="Approve"
              onConfirm={async () => {
                try {
                  await adminApprovePartner(r.id)
                  message.success("Approved")
                  load()
                } catch {
                  message.error("Failed")
                }
              }}
            >
              <Button type="primary" size="small">
                Approve
              </Button>
            </Popconfirm>
          )}
          {r.status === "approved" && (
            <Popconfirm
              title="Suspend this affiliate? Their affiliate features pause."
              okText="Suspend"
              okButtonProps={{ danger: true }}
              onConfirm={async () => {
                try {
                  await adminSuspendPartner(r.id)
                  message.success("Suspended")
                  load()
                } catch {
                  message.error("Failed")
                }
              }}
            >
              <Button danger size="small">
                Suspend
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ]

  return (
    <div className="p-6">
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-xl font-semibold">
          Partners{" "}
          {pendingCount > 0 && (
            <Tag color="gold">{pendingCount} pending</Tag>
          )}
        </h1>
        <Button type="primary" onClick={() => setCreateOpen(true)}>
          New partner
        </Button>
      </div>
      <Segmented
        className="mb-3"
        value={statusFilter}
        onChange={(v) => setStatusFilter(v as typeof statusFilter)}
        options={[
          { label: "All", value: "all" },
          { label: "Pending", value: "pending" },
          { label: "Approved", value: "approved" },
          { label: "Suspended", value: "suspended" },
        ]}
      />
      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={filteredRows}
        pagination={{
          current: page,
          pageSize: 30,
          total,
          onChange: (p) => setPage(p),
        }}
      />

      <Modal
        title="Create partner"
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        okText="Create"
        onOk={async () => {
          try {
            const v = await form.validateFields()
            await adminCreatePartner({
              name: v.name,
              slug: v.slug,
              contactEmail: v.contactEmail,
              isActive: true,
              notes: v.notes,
            })
            message.success("Created")
            setCreateOpen(false)
            form.resetFields()
            load()
          } catch {
            message.error("Failed")
          }
        }}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item
            name="slug"
            label="Slug (lowercase, hyphens)"
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="contactEmail" label="Contact email">
            <Input type="email" />
          </Form.Item>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}
